import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react'
import ReconnectingWebSocket from 'reconnecting-websocket'
import config from '@/config'
import { EventTypes, ReadyState } from './constants'
import request from '../base/util/request'
import { bidiLog } from '../base/util/bidiLog'
import { ackUiEvent } from '@/modules/document/utils/ui-event-ack'

const orgamax = require('ampersand-app')

export interface WebSocketEvent {
  /** name of event */
  type: string
  /** message data object */
  data: unknown
}

/**
 * Callback function type for WebSocket event handlers
 */
export type OnEventCallback<TEvent extends WebSocketEvent = WebSocketEvent> = (event: TEvent) => void

interface WebSocketContextValue {
  /** Send a message through the WebSocket */
  sendMessage: (type: string, data: unknown) => void
  /** Current WebSocket ready state */
  readyState: number
  /** Whether the WebSocket is currently connected */
  isConnected: boolean
  /** Subscribe to WebSocket events */
  subscribe: <TEvent extends WebSocketEvent = WebSocketEvent>(eventFilters: string | string[], callback: OnEventCallback<TEvent>) => () => void
  /**
   * Monotonic counter bumped on every (re)open of the socket. 1 = first connect, >1 = a reconnect.
   * Consumers watch this to resync state that may have changed while the socket was down (the
   * server broadcasts are fire-and-forget with no replay). See the AI-badge reconnect resync.
   */
  connectionEpoch: number
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

const messageQueue: Array<WebSocketEvent> = []

let fallBackHandler: NodeJS.Timeout | null = null

function fallbackCheck(timeout = 10000): NodeJS.Timeout {
  return setTimeout(async() => {
    const store = require('@/redux/store').default
    const { isOffline, setApiVersion } = require('@/redux/ducks/global')

    try {
      const response = await request(`${config.resourceHost}healthcheck`, { method: 'GET' })
      if (response.statusCode === 200) {
        const { global } = store.getState()
        if (global.isOffline) {
          store.dispatch(isOffline(false))
        }
        const { body: { version } } = response
        if (global.apiVersion !== version) {
          store.dispatch(setApiVersion(version))
        }
        if (global.isWSOffline) {
          fallBackHandler = fallbackCheck()
        }
      } else {
        throw Error('healthcheck failed')
      }
    } catch {
      !store.getState().global.isOffline && store.dispatch(isOffline(true))
      fallBackHandler = fallbackCheck()
    }
  }, timeout)
}

function useFallbackCheck() {
  if (fallBackHandler) {
    return
  }
  fallBackHandler = fallbackCheck(1000)
}

interface Subscription {
  id: number
  eventFilters: string[]
  callback: OnEventCallback
}

let subscriptionIdCounter = 0

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const webSocketRef = useRef<ReconnectingWebSocket | null>(null)
  const subscriptionsRef = useRef<Map<number, Subscription>>(new Map())
  const [readyState, setReadyState] = useState<number>(ReadyState.UNINSTANTIATED)
  const [connectionEpoch, setConnectionEpoch] = useState<number>(0)
  const activeTokenRef = useRef<string>()
  const isConnected = readyState === ReadyState.OPEN

  // Central message handler that distributes to all subscribers
  const handleMessage = useCallback(({ data: message }: MessageEvent) => {
    try {
      const messageEvent = JSON.parse(message)

      // FLAG:useDocuAi — GLOBAL RECEIPT ACK for the UI-delivery guarantee
      // (plans/usedocuai-guaranteed-ui-delivery, T7). This is the only always-mounted point
      // in the app that sees every incoming frame, which is exactly why the ack belongs here.
      //
      // The six per-host acks (Documents list, expense list, drawer, review,
      // embedded review, receipt-edit) are all PAGE-SPECIFIC. A user sitting anywhere else
      // when an analysis finishes means nobody acks — so the server burns its whole resend
      // budget and then logs a warn asserting a defect, for a situation where nothing is
      // wrong. That made the ledger's "unacked" state meaningless and the warning a false
      // alarm. Acking receipt here restores both.
      //
      // SEMANTICS, deliberately: for STOPPING the resend, "received" now counts — not
      // "processed". The resend exists to recover LOST messages, not to compensate for a page
      // having no interested component. The per-host acks stay as the "processed" signal and
      // cost nothing extra: ackUiEvent de-duplicates by eventId and coalesces into one
      // request. Badge correctness does not depend on this ack: ws delivery is per SOCKET, so
      // every mounted component receives the same frame and patches synchronously — this can
      // never silence a resend a list still needed.
      //
      // Own try/catch: ackUiEvent cannot throw today (it guards its input, then Set.add +
      // setTimeout), but the enclosing catch reports "message parsing error", and a delivery
      // ack must never be able to masquerade as that.
      try {
        ackUiEvent(messageEvent?.data?.eventId)
      } catch { /* never let bookkeeping break message distribution */ }

      // Handle CONNECT event for version sync
      if (messageEvent.type === EventTypes.CONNECT) {
        const { data: { version } } = messageEvent
        const store = require('@/redux/store').default
        const { setApiVersion } = require('@/redux/ducks/global')
        store.dispatch(setApiVersion(version))
      }

      // Distribute message to all matching subscribers
      subscriptionsRef.current.forEach((subscription) => {
        const { eventFilters, callback } = subscription
        const isIncluded = eventFilters.some((filter) =>
          messageEvent.type.startsWith(filter),
        )
        if (isIncluded) {
          callback(messageEvent)
        }
      })
    } catch (error) {
      console.error('WebSocket message parsing error:', error)
    }
  }, [])

  const handleError = useCallback(() => {
    useFallbackCheck()
    const store = require('@/redux/store').default
    const { isWSOffline } = require('@/redux/ducks/global')
    !store.getState().global.isWSOffline && store.dispatch(isWSOffline(true))
  }, [])

  const handleOpen = useCallback(() => {
    setReadyState(ReadyState.OPEN)
    // Bump the epoch on every (re)open so consumers can resync state missed while the socket was
    // down. Functional update keeps this useCallback dependency-free.
    setConnectionEpoch((epoch) => {
      const next = epoch + 1
      bidiLog('ws:open', { epoch: next }, next > 1 ? 'reconnect' : 'first-connect')
      return next
    })

    const store = require('@/redux/store').default
    const { isOffline, isWSOffline } = require('@/redux/ducks/global')
    const { global } = store.getState()

    global.isOffline && store.dispatch(isOffline(false))
    global.isWSOffline && store.dispatch(isWSOffline(false))

    // Process queued messages
    if (messageQueue.length > 0) {
      messageQueue.splice(0).forEach(msg => {
        sendMessage(msg.type, msg.data)
      })
    }
  }, [])

  const handleClose = useCallback(() => {
    setReadyState(ReadyState.CLOSED)
    bidiLog('ws:close', {}, 'socket closed')
  }, [])

  // Initialize WebSocket connection
  useEffect(() => {
    const init = () => {
      const token = orgamax.user?.token

      if (!token || activeTokenRef.current === token) {
        return
      }

      // Close existing connection if token changed
      if (webSocketRef.current) {
        webSocketRef.current.close()
        webSocketRef.current.removeEventListener('open', handleOpen)
        webSocketRef.current.removeEventListener('close', handleClose)
        webSocketRef.current.removeEventListener('message', handleMessage)
        webSocketRef.current.removeEventListener('error', handleError)
      }

      activeTokenRef.current = token
      const url = `${config.webSocketUrl}?token=${token}`

      const ws = new ReconnectingWebSocket(url, undefined, {
        connectionTimeout: 5000,
      })

      ws.addEventListener('open', handleOpen)
      ws.addEventListener('close', handleClose)
      ws.addEventListener('message', handleMessage)
      ws.addEventListener('error', handleError)

      webSocketRef.current = ws
      setReadyState(ws.readyState)
    }

    orgamax.user?.on('change', init)

    return () => {
      orgamax.user?.off('change', init)

      webSocketRef.current?.removeEventListener('open', handleOpen)
      webSocketRef.current?.removeEventListener('close', handleClose)
      webSocketRef.current?.removeEventListener('message', handleMessage)
      webSocketRef.current?.removeEventListener('error', handleError)
      webSocketRef.current?.close()
    }
  }, [])

  const sendMessage = useCallback((type: string, data: unknown) => {
    const msg = { type, data }

    if (webSocketRef.current && webSocketRef.current.readyState === ReadyState.OPEN) {
      webSocketRef.current.send(JSON.stringify(msg))
    } else {
      messageQueue.push(msg)
    }
  }, [])

  const subscribe = useCallback(<TEvent extends WebSocketEvent = WebSocketEvent>(eventFilters: string | string[], callback: OnEventCallback<TEvent>): (() => void) => {
    const id = subscriptionIdCounter++
    const filters = Array.isArray(eventFilters) ? eventFilters : [eventFilters]

    subscriptionsRef.current.set(id, {
      id,
      eventFilters: filters,
      callback,
    })

    // Return unsubscribe function
    return () => {
      subscriptionsRef.current.delete(id)
    }
  }, [])

  const value: WebSocketContextValue = useMemo(() => ({
    sendMessage,
    readyState,
    isConnected,
    subscribe,
    connectionEpoch,
  }), [readyState, isConnected, connectionEpoch])

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

/**
 * Hook to access WebSocket context directly
 * @returns WebSocketContextValue
 */
export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
